import React, { useRef, useState, useEffect } from "react";
import "./OnlineLearningSection.css";
import * as myConstClass from "../constant";
import { getLanguage } from 'react-switch-lang';
import en from '../en.json';
import ma from '../ma.json';

const translationData = { en: en, mr: ma };

const extractYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/.*[?&]v=([^&\n?#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const getYouTubeThumbnail = (url) => {
  const id = extractYouTubeVideoId(url);
  if (!id) return "";

  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
};

const OnlineLearningSection = () => {
  const sliderRef = useRef(null);
  const link = "https://onlinecourses.manashakti.org/";

  const [language, setLanguage] = useState(() => getLanguage() || 'en');
  const t = (key) => {
    const langKey = language === 'ma' || language === 'mr' ? 'mr' : 'en';
    return translationData[langKey]?.[key] || '';
  };
  useEffect(() => {
    const checkLang = () => {
      const current = getLanguage() || 'en';
      setLanguage(prev => prev !== current ? current : prev);
    };
    checkLang();
    const interval = setInterval(checkLang, 500);
    window.addEventListener('storage', checkLang);
    return () => { clearInterval(interval); window.removeEventListener('storage', checkLang); };
  }, []);

  const languageId = (language === 'ma' || language === 'mr') ? 2 : 1;

  const [videos, setVideos] = useState([]);

  useEffect(() => {
    const fetchIntroVideos = async () => {
      try {
        const res = await fetch(
          `${myConstClass.NODE_API_BASE}/video/intro-playlist/videos?languageId=${languageId}`
        );
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          setVideos(data.data);
          console.log("FULL VIDEO DATA:", data.data);
        } else {
          setVideos([]);
        }
      } catch (error) {
        console.error("Error fetching intro videos:", error);
        setVideos([]);
      }
    };

    fetchIntroVideos();
  }, [languageId]);

  const scrollLeft = () => {
    const slider = sliderRef.current;
    const card = slider.querySelector(".video-card");
    if (!card) return;

    const scrollAmount = card.offsetWidth + 24;

    const lastCard = slider.lastElementChild;
    slider.prepend(lastCard);
    slider.scrollLeft += scrollAmount;

    slider.scrollBy({
      left: -scrollAmount,
      behavior: "smooth",
    });
  };

  const scrollRight = () => {
    const slider = sliderRef.current;
    const card = slider.querySelector(".video-card");
    if (!card) return;

    const scrollAmount = card.offsetWidth + 24;

    slider.scrollBy({
      left: scrollAmount,
      behavior: "smooth",
    });

    setTimeout(() => {
      const firstCard = slider.firstElementChild;
      slider.appendChild(firstCard);
      slider.scrollLeft -= scrollAmount;
    }, 400);
  };

  return (
    <section className="online-section">
      <div className="online-container">

        <div className="online-left-card">
          <h3>{t('online_title')}</h3>

          <p className="online-description">
            {t('online_attend_line1')}<br />
            {t('online_attend_line2')}
          </p>

          <p className="limited-text">{t('online_limited')}</p>

          <p className="portal-text">
            {t('online_portal')}
          </p>

          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="access-btn"
          >
            {t('online_access_btn')}
          </a>

          <div className="arrow-icon">
            <a href={link} target="_blank" rel="noopener noreferrer">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  stroke="#9d0b0e"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>
        </div>

        <div className="online-right">
          <h3 className="intro-title">
            {t('online_free_learning')}
          </h3>
          <div className="slider-wrapper">
            <div
              className="video-slider with-scrollbar"
              ref={sliderRef}
              style={{
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
                msOverflowStyle: 'auto',
                paddingBottom: 8,
                display: 'flex',
                gap: 24,
              }}
            >
              {videos.length === 0 ? (
                <p style={{ padding: '20px', color: '#666' }}>Videos will be available soon.</p>
              ) : (
                videos.map((video) => {
                  // Use playlistUrl for the link as per requirement
                  const playlistUrl = video.playlistUrl || "";
                  const title = video.title || video.titleMr || '';
                  // Use backend thumbnail if available, else fallback to YouTube thumbnail (use firstVideoUrl for thumbnail fallback)
                  const thumbnailUrl = video.thumbnail || getYouTubeThumbnail(video.firstVideoUrl || "");
                  return (
                    <a
                      href={playlistUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="video-card"
                      key={video.id || video.videoId}
                      style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer', minWidth: 260, maxWidth: 320, flex: '0 0 auto' }}
                    >
                      <div className="video-thumbnail" style={{
                        backgroundImage: `url(${thumbnailUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}>
                        <div className="play-circle">▶</div>
                      </div>
                      <div className="video-info">
                        <p style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minHeight: '4.2em', // increased for better matra rendering
                          maxHeight: '5.4em', // increased for better matra rendering
                          lineHeight: '1.4em', // increased for better matra rendering
                          wordBreak: 'break-word',
                        }}><span style={{ fontWeight: 450 }}>{title}</span></p>
                      </div>
                    </a>
                  );
                })
              )}
            </div>
            {/* Arrows below slider, hidden on mobile */}
            <div className="arrow-row hide-on-mobile">
              <button
                className="carousel-arrow left-arrow"
                onClick={scrollLeft}
                aria-label="Scroll left"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                className="carousel-arrow right-arrow"
                onClick={scrollRight}
                aria-label="Scroll right"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OnlineLearningSection;